import { useState } from "react";
import { timeEntryService } from "@/services/timeEntryService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { formatDate } from "@/utils/formatters";
import { toast } from "sonner";
import { Timer, X } from "lucide-react";

export function TimeTrackingSection({ timeEntries, timeSummary, user, ticketId, fetchTimeEntries, refreshActivity }) {
  const [timeMinutes, setTimeMinutes] = useState("");
  const [timeDesc, setTimeDesc] = useState("");

  const handleLogTime = async () => {
    const mins = parseInt(timeMinutes, 10);
    if (!mins || mins <= 0) { toast.error("Enter valid minutes"); return; }
    try {
      await timeEntryService.create(ticketId, { minutes: mins, description: timeDesc });
      toast.success("Time logged"); setTimeMinutes(""); setTimeDesc(""); fetchTimeEntries();
      await refreshActivity();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDeleteTimeEntry = async (entryId) => {
    try { await timeEntryService.delete(ticketId, entryId); toast.success("Entry deleted"); fetchTimeEntries(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <>
      {timeSummary && timeSummary.total_minutes > 0 && (
        <Card className="border-[#E4E4E7] shadow-none rounded-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-4">
              <div><span className="text-lg font-bold text-[#09090B]">{timeSummary.total_hours}h</span><span className="text-xs text-[#52525B] ml-1">total</span></div>
              <Separator orientation="vertical" className="h-8" />
              <div className="flex flex-wrap gap-2">
                {timeSummary.by_user?.map((u) => (
                  <span key={u.user_id} className="text-xs text-[#52525B]"><span className="font-medium text-[#09090B]">{u.user_name}</span>: {u.minutes}m ({u.entries} entries)</span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {user?.role !== "end_user" && (
        <Card className="border-[#E4E4E7] shadow-none rounded-sm">
          <CardContent className="p-3 flex items-end gap-2">
            <div className="w-20">
              <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#52525B]">Minutes</Label>
              <input type="number" min="1" value={timeMinutes} onChange={(e) => setTimeMinutes(e.target.value)} placeholder="30" className="w-full h-8 rounded-sm border border-zinc-300 px-2 text-sm" data-testid="time-minutes-input" />
            </div>
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#52525B]">Description</Label>
              <input type="text" value={timeDesc} onChange={(e) => setTimeDesc(e.target.value)} placeholder="What did you work on?" className="w-full h-8 rounded-sm border border-zinc-300 px-2 text-sm" data-testid="time-desc-input" />
            </div>
            <Button onClick={handleLogTime} className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white h-8 text-xs gap-1" data-testid="log-time-btn"><Timer size={13} /> Log</Button>
          </CardContent>
        </Card>
      )}
      {timeEntries.length === 0 && <p className="text-sm text-[#52525B] py-4 text-center" data-testid="no-time-entries">No time entries yet</p>}
      {timeEntries.map((entry) => (
        <div key={entry.id} className="flex items-center gap-2 py-2 border-b border-[#E4E4E7] text-sm" data-testid={`time-entry-${entry.id}`}>
          <Timer size={13} className="text-[#52525B] shrink-0" />
          <span className="font-medium text-[#09090B]">{entry.minutes}m</span>
          <span className="text-[#52525B] flex-1 truncate">{entry.description || "—"}</span>
          <span className="text-xs text-[#52525B]">{entry.user_name}</span>
          <span className="text-xs text-[#52525B]">{formatDate(entry.created_at)}</span>
          {(entry.user_id === user?.id || user?.role === "admin") && (
            <button onClick={() => handleDeleteTimeEntry(entry.id)} className="text-red-500 hover:text-red-600 p-0.5" data-testid={`delete-time-${entry.id}`}><X size={12} /></button>
          )}
        </div>
      ))}
    </>
  );
}
